import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { commonDataGridStyles } from '@rossum/components/data-grid/styles';
import { DataGridPro, gridClasses } from '@rossum/ui/x-data-grid-pro';
import { SchemaWithEtag } from '../../../../../business/schema/useSchema';
import { absoluteMaxSizeStyles } from '../../../../document-upload/components/helpers';
import { getRowId } from '../../core/utils';
import {
  NEW_THRESHOLD_INPUT_CLASS_NAME,
  useLegacyThresholdsTableColumns,
} from '../hooks';
import {
  LegacyAutomationSettingsFormInput,
  LegacyThresholdsTableRowModel,
} from '../types';
import { getLegacyThresholdsRows } from '../utils';

type LegacyAutomationThresholdsTableProps = {
  isLoading: boolean;
  queueSchema: SchemaWithEtag | undefined;
  defaultScoreThreshold: number;
};

export const LegacyAutomationThresholdsTable = ({
  isLoading,
  queueSchema,
  defaultScoreThreshold,
}: LegacyAutomationThresholdsTableProps) => {
  const columns = useLegacyThresholdsTableColumns();

  const {
    formState: { errors },
  } = useFormContext<LegacyAutomationSettingsFormInput>();

  const rows = useMemo<LegacyThresholdsTableRowModel[]>(() => {
    const { content } = queueSchema ?? {};
    if (!content) return [];
    const transformedRows = getLegacyThresholdsRows({
      schemaContent: content,
      defaultScoreThreshold,
    });
    return transformedRows.map(row => {
      const errorMessage = errors.thresholdValues?.[row.schemaId]?.message;

      return {
        ...row,
        errorMessage,
      };
    });
  }, [queueSchema, defaultScoreThreshold, errors.thresholdValues]);

  return (
    <DataGridPro
      loading={isLoading}
      disableColumnMenu
      disableMultipleRowSelection
      disableRowSelectionOnClick
      disableColumnReorder
      columns={columns}
      rows={rows}
      getRowId={getRowId}
      slots={{ footer: () => null }}
      sx={{
        ...commonDataGridStyles,
        ...absoluteMaxSizeStyles,
        [`& .${gridClasses.row}:hover`]: {
          [`& .${NEW_THRESHOLD_INPUT_CLASS_NAME}`]: {
            display: 'block',
          },
        },
      }}
    />
  );
};
